<template>
    <Card pageTitle="Customer">
        <div>
            <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_CUSTOMER">
                <CustomerListToolbar
                    :search-str.sync="searchStr"
                    :search-type.sync="searchType"
                    :filter-options="filterOptions"
                    :filters="filters"
                    :customer-groups="customerGroups"
                    :secondary-sort-field.sync="secondarySortField"
                    :secondary-sort-direction.sync="secondarySortDirection"
                    :sort-field.sync="sortField"
                    :sort-direction.sync="sortDirection"
                    :available-sort-fields="visibleSortFields"
                    :show-loyalty="canViewLoyalty"
                    :exporting="exportingCustomers"
                    :limit.sync="limit"
                    :format-sort-field-label="formatSortFieldLabel"
                    @search="handleSearch"
                    @search-input="onSearchInput"
                    @clear-search="clearSearch"
                    @search-type-change="onSearchTypeChange"
                    @update-filter="updateFilter"
                    @reset-sort="resetSort"
                    @apply-filters="applyFilters"
                    @limit-change="handleLimitChange"
                    @export="exportCustomers"
                />

                <CustomerStatusBar
                    v-if="!loadError"
                    :loading="loading"
                    :total-count="totalCount"
                    :page="page"
                    :limit="limit"
                    :is-filtered="!!isFiltered"
                    :search-str="searchStr"
                    :search-type.sync="searchType"
                    :filter-options="filterOptions"
                    :filters="filters"
                    :customer-groups="customerGroups"
                    :sort-field="sortField"
                    :sort-direction="sortDirection"
                    :secondary-sort-field="secondarySortField"
                    :secondary-sort-direction="secondarySortDirection"
                    :format-sort-field-label="formatSortFieldLabel"
                    @clear-search="clearSearch"
                    @update-filter="updateFilter"
                    @refresh="refreshFromFilterChange"
                    @reset-sort="resetSort"
                    @reset-secondary-sort="resetSecondarySort"
                    @reset-filters="resetFilters"
                />

                <UiState v-if="loading" type="loading" title="Loading customers" />
                <UiState
                    v-else-if="loadError"
                    type="error"
                    title="Could not load customers"
                    :message="loadError"
                    action-label="Retry"
                    @action="fetchData"
                />
                <CustomerResultsTable
                    v-else-if="customers.length"
                    :customers="customers"
                    :columns="visibleColumns"
                    :page="page"
                    :limit="limit"
                    :sort-field="sortField"
                    :sort-direction="sortDirection"
                    :secondary-sort-field="secondarySortField"
                    :secondary-sort-direction="secondarySortDirection"
                    :loading="loading"
                    @sort="sortBy"
                    @edit="editCustomer"
                    @manage-loyalty="manageCustomerLoyalty"
                    @delete="deleteCustomer"
                />
                <UiState
                    v-else
                    type="empty"
                    title="No customers found"
                    message="Try changing your search or filter criteria."
                    action-label="Reset Filters"
                    @action="resetFilters"
                />

                <!-- Pagination -->
                <div class="pagination-container" v-if="!loading && !loadError && totalPages > 0">
                    <div class="pagination-info">
                        Page {{ page }} of {{ totalPages }}
                    </div>
                    <Paginator @change="updatePage" :page="page" :totalPages="totalPages" />
                </div>
            </AllowPermission>
        </div>
    </Card>
</template>

<script>
import { confirmDialog, usFormatMobileNo } from "../../utils/common"
import { mapState, mapActions } from "vuex"
import { http } from "@/utils/http"
import Paginator from "../../components/other/Paginator.vue"
import Card from "../../components/UI/Card.vue"
import AllowPermission from "../../components/layout/AllowPermission.vue"
import { PERMISSION_CODES, checkHasPermission } from "../../store/permission"
import CustomerListToolbar from "../../components/customers/CustomerListToolbar.vue"
import CustomerStatusBar from "../../components/customers/CustomerStatusBar.vue"
import CustomerResultsTable from "../../components/customers/CustomerResultsTable.vue"
import UiState from "../../components/UI/UiState.vue"
import { customerEditPath, customerLoyaltyPath } from "../../router/route-paths"

export default {
    data() {
        return {
            searchStr: "",
            searchType: "",
            loading: false,
            loadError: "",
            totalPages: 0,
            totalCount: 0,
            page: 1,
            limit: 25,
            customers: [],
            exportingCustomers: false,
            searchTimer: null,
            sortField: "createdAt",
            sortDirection: "desc",
            secondarySortField: "last_name",
            secondarySortDirection: "asc",
            filters: {
                groupId: "",
                dateFrom: null,
                dateTo: null,
                status: "",
                verified: "",
                minPoints: null,
                maxPoints: null
            },
            columns: [
                {
                    label: "First Name",
                    field: "first_name",
                    sortable: true,
                },
                {
                    label: "Last Name",
                    field: "last_name",
                    sortable: true,
                },
                {
                    label: "Email",
                    field: "email_id",
                    sortable: true,
                },
                {
                    label: "Mobile No",
                    field: "mobile_no",
                    sortable: true,
                },
                {
                    label: "Customer Group",
                    field: "customer_group_name",
                    sortable: false,
                },
                {
                    label: "ID Verified",
                    field: "id_verification_status",
                    sortable: false,
                },
                {
                    label: "Current Points",
                    field: "loyalty_points_balance",
                    sortable: true,
                },
            ],
            PERMISSION_CODES,
            availableSortFields: [
                "id", "first_name", "last_name", "customer_name",
                "email_id", "mobile_no", "phone_no", "createdAt",
                "updatedAt", "loyalty_points_balance"
            ],
            filterOptions: {
                status: [
                    { label: 'All', value: '' },
                    { label: 'Active', value: 'active' },
                    { label: 'Inactive', value: 'inactive' }
                ],
                verified: [
                    { label: 'All', value: '' },
                    { label: 'Verified', value: 'true' },
                    { label: 'Not Verified', value: 'false' }
                ],
                searchType: [
                    { label: 'All Fields', value: '' },
                    { label: 'Name', value: 'name' },
                    { label: 'Email', value: 'email' },
                    { label: 'Mobile Number', value: 'mobile_no' },
                    { label: 'Government ID', value: 'govt_id' }
                ]
            },
        }
    },
    components: {
        Card,
        Paginator,
        AllowPermission,
        CustomerListToolbar,
        CustomerStatusBar,
        CustomerResultsTable,
        UiState,
    },
    async mounted() {
        // Get the page from URL if available
        if (this.$route.query.page) {
            this.page = parseInt(this.$route.query.page) || 1;
        }

        // Get the limit from URL if available
        if (this.$route.query.limit) {
            this.limit = parseInt(this.$route.query.limit) || 25;
        }

        // Get the sort field and direction from URL if available
        if (this.$route.query.sort && this.visibleSortFields.includes(this.$route.query.sort)) {
            this.sortField = this.$route.query.sort;
        }

        if (this.$route.query.direction) {
            this.sortDirection = this.$route.query.direction;
        }

        // Get the search string from URL if available
        if (this.$route.query.search) {
            this.searchStr = this.$route.query.search;
        }

        // Get the search type from URL if available
        if (this.$route.query.search_type) {
            this.searchType = this.$route.query.search_type;
        }

        // Get all filters from URL if available
        if (this.$route.query.groupId) {
            this.filters.groupId = this.$route.query.groupId;
        }

        if (this.$route.query.dateFrom) {
            this.filters.dateFrom = this.$route.query.dateFrom;
        }

        if (this.$route.query.dateTo) {
            this.filters.dateTo = this.$route.query.dateTo;
        }

        if (this.$route.query.status) {
            this.filters.status = this.$route.query.status;
        }

        if (this.$route.query.verified) {
            this.filters.verified = this.$route.query.verified;
        }

        if (this.canViewLoyalty && this.$route.query.minPoints !== undefined) {
            this.filters.minPoints = this.$route.query.minPoints;
        }

        if (this.canViewLoyalty && this.$route.query.maxPoints !== undefined) {
            this.filters.maxPoints = this.$route.query.maxPoints;
        }

        // Check if customer groups are already loaded from Vuex
        if (!this.customerGroups || this.customerGroups.length === 0) {
            await this.fetchMainMasters({ scope: "customers" });
        }

        this.fetchData();
    },
    watch: {
        page() {
            this.updateUrl();
            this.fetchData();
        }
    },
    computed: {
        ...mapState("misc", ["showSidebar", "countries", "customerGroups"]),
        ...mapState("auth", ["user"]),

        canViewLoyalty() {
            return checkHasPermission({
                isAdmin: Boolean(this.user?.isAdmin),
                permissionId: PERMISSION_CODES.CMS_VIEW_LOYALTY,
                userPermissionIds: this.user?.permissionIds || [],
            })
        },

        visibleColumns() {
            return this.canViewLoyalty
                ? this.columns
                : this.columns.filter(column => column.field !== "loyalty_points_balance")
        },

        visibleSortFields() {
            return this.canViewLoyalty
                ? this.availableSortFields
                : this.availableSortFields.filter(field => field !== "loyalty_points_balance")
        },

        isFiltered() {
            return this.searchStr ||
                this.filters.groupId ||
                this.filters.dateFrom ||
                this.filters.dateTo ||
                this.filters.status ||
                this.filters.verified ||
                this.filters.minPoints !== null && this.filters.minPoints !== "" ||
                this.filters.maxPoints !== null && this.filters.maxPoints !== "" ||
                this.sortField !== 'createdAt' ||
                this.sortDirection !== 'desc' ||
                (this.secondarySortField && this.secondarySortField !== 'last_name') ||
                (this.secondarySortField && this.secondarySortDirection !== 'asc');
        }
    },
    methods: {
        ...mapActions("misc", ["fetchCountries", "fetchMainMasters"]),

        updateFilter({ field, value }) {
            this.$set(this.filters, field, value)
        },

        refreshFromFilterChange() {
            this.page = 1
            this.updateUrl()
            this.fetchData()
        },

        editCustomer(id) {
            this.$router.push(customerEditPath(id))
        },

        manageCustomerLoyalty(id) {
            this.$router.push(customerLoyaltyPath(id))
        },

        formatSortFieldLabel(field) {
            const labelMap = {
                'id': 'ID',
                'first_name': 'First Name',
                'last_name': 'Last Name',
                'customer_name': 'Full Name',
                'email_id': 'Email',
                'mobile_no': 'Mobile Number',
                'phone_no': 'Phone Number',
                'createdAt': 'Created Date',
                'updatedAt': 'Last Updated',
                'loyalty_points_balance': 'Current Points'
            };

            return labelMap[field] || field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        },

        updateUrl() {
            // Update URL with current filters/pagination without reloading page
            const query = {
                page: this.page,
                limit: this.limit
            };

            if (this.searchStr) {
                query.search = this.searchStr.trim();
            }

            if (this.searchType) {
                query.search_type = this.searchType;
            }

            // Include sort parameters
            query.sort = this.sortField;
            query.direction = this.sortDirection;

            // Include secondary sort if different from default
            if (this.secondarySortField && this.secondarySortField !== 'last_name') {
                query.secondary_sort = this.secondarySortField;
            }
            if (this.secondarySortDirection && this.secondarySortDirection !== 'asc') {
                query.secondary_direction = this.secondarySortDirection;
            }

            // Include all filter parameters
            if (this.filters.groupId) {
                query.groupId = this.filters.groupId;
            }

            if (this.filters.dateFrom) {
                query.dateFrom = this.filters.dateFrom;
            }

            if (this.filters.dateTo) {
                query.dateTo = this.filters.dateTo;
            }

            if (this.filters.status) {
                query.status = this.filters.status;
            }

            if (this.filters.verified) {
                query.verified = this.filters.verified;
            }

            if (this.canViewLoyalty && this.filters.minPoints !== null && this.filters.minPoints !== '') {
                query.minPoints = this.filters.minPoints;
            }

            if (this.canViewLoyalty && this.filters.maxPoints !== null && this.filters.maxPoints !== '') {
                query.maxPoints = this.filters.maxPoints;
            }

            this.$router.replace({ query });
        },

        updatePage(e) {
            if (e > 0 && e <= this.totalPages) {
                this.page = e;
            } else {
                this.$toast.error("Invalid page!");
            }
        },

        handleLimitChange() {
            this.page = 1;
            this.updateUrl();
            this.fetchData();
        },

        handleSearch() {
            this.page = 1;
            this.updateUrl();
            this.fetchData();
        },

        onSearchInput() {
            // Clear any existing timer
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }

            // Set a new timer to delay the search execution
            this.searchTimer = setTimeout(() => {
                this.page = 1;
                this.updateUrl();
                this.fetchData();
            }, 500); // 500ms delay for debouncing
        },

        onSearchTypeChange() {
            this.page = 1;
            this.updateUrl();
            this.fetchData();
        },

        clearSearch() {
            this.searchStr = "";
            this.searchType = "";
            this.page = 1;
            this.updateUrl();
            this.fetchData();
        },

        sortBy(field) {
            // If clicking on the same field, toggle direction
            if (this.sortField === field) {
                this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
            } else {
                // If selecting a new field, set it as primary sort and move current primary to secondary
                this.secondarySortField = this.sortField;
                this.secondarySortDirection = this.sortDirection;
                this.sortField = field;
                this.sortDirection = 'asc';
            }

            this.page = 1;
            this.updateUrl();
            this.fetchData();
        },

        resetSort() {
            this.sortField = 'createdAt';
            this.sortDirection = 'desc';
            this.secondarySortField = 'last_name';
            this.secondarySortDirection = 'asc';
            this.page = 1;
            this.updateUrl();
            this.fetchData();
        },

        applyFilters() {
            this.page = 1;
            this.updateUrl();
            this.fetchData();
        },

        resetFilters() {
            this.searchStr = "";
            this.filters = {
                groupId: "",
                dateFrom: null,
                dateTo: null,
                status: "",
                verified: "",
                minPoints: null,
                maxPoints: null
            };
            this.sortField = "createdAt";
            this.sortDirection = "desc";
            this.page = 1;
            this.updateUrl();
            this.fetchData();
        },

        resetSecondarySort() {
            this.secondarySortField = 'last_name';
            this.secondarySortDirection = 'asc';
            this.updateUrl();
            this.fetchData();
        },

        buildCustomerQueryParams(includePagination = true) {
            const params = new URLSearchParams()
            if (includePagination) {
                params.append("start", (this.page - 1) * this.limit)
                params.append("limit", this.limit)
            }

            if (this.searchStr) {
                params.append("search", this.searchStr.trim())
            }
            if (this.searchType) {
                params.append("search_type", this.searchType)
            }

            params.append("sort", this.sortField)
            params.append("direction", this.sortDirection)
            if (this.secondarySortField) {
                params.append("secondary_sort", this.secondarySortField)
                params.append("secondary_direction", this.secondarySortDirection)
            }

            if (this.filters.groupId) {
                params.append("customer_group", this.filters.groupId)
            }
            if (this.filters.dateFrom) {
                params.append("date_from", new Date(this.filters.dateFrom).toISOString().split("T")[0])
            }
            if (this.filters.dateTo) {
                params.append("date_to", new Date(this.filters.dateTo).toISOString().split("T")[0])
            }
            if (this.filters.status) {
                params.append("status", this.filters.status)
            }
            if (this.filters.verified) {
                params.append("verified", this.filters.verified)
            }
            if (this.canViewLoyalty && this.filters.minPoints !== null && this.filters.minPoints !== "") {
                params.append("min_points", this.filters.minPoints)
            }
            if (this.canViewLoyalty && this.filters.maxPoints !== null && this.filters.maxPoints !== "") {
                params.append("max_points", this.filters.maxPoints)
            }
            return params
        },

        async exportCustomers() {
            if (this.exportingCustomers) return

            try {
                this.exportingCustomers = true
                const params = this.buildCustomerQueryParams(false)
                const response = await http.get(`/customers/export?${params.toString()}`, {
                    responseType: "blob",
                })
                const contentDisposition = response.headers && response.headers["content-disposition"]
                const filenameMatch = contentDisposition && contentDisposition.match(/filename="?([^";]+)"?/)
                const filename = filenameMatch ? filenameMatch[1] : "customers.csv"
                const blobUrl = window.URL.createObjectURL(
                    new Blob([response.data], { type: response.headers["content-type"] || "text/csv;charset=utf-8" })
                )
                const link = document.createElement("a")
                link.href = blobUrl
                link.download = filename
                link.rel = "noopener noreferrer"
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(blobUrl)
                this.$toast.success("Customer export downloaded.")
            } catch (err) {
                console.error("Error exporting customers", err)
                this.$toast.error("Failed to export customers.")
            } finally {
                this.exportingCustomers = false
            }
        },

        async deleteCustomer(id) {
            try {
                const confirmed = await confirmDialog({
                    title: "Delete customer?",
                    text: "This customer profile will be permanently removed.",
                    confirmButtonText: "Delete",
                });
                if (!confirmed) return;

                await http.delete(`/customers/${id}`);
                this.$toast.success("Customer deleted successfully!");

                // If we're on the last page and deleted the last item, go to previous page
                if (this.customers.length === 1 && this.page > 1) {
                    this.page -= 1;
                } else {
                    this.fetchData();
                }
            } catch (err) {
                console.error("Error deleting customer", err);
                this.$toast.error(err.response?.data?.message || "Failed to delete customer");
            }
        },

        getFormatter(data) {
            return data.map((item) => {
                if (item.mobile_no) {
                    item.mobile_no = usFormatMobileNo(item.mobile_no);
                }
                item.id_verification_status = item.government_id && item.government_id_number ? "Verified" : "Not Verified";
                return item;
            });
        },

        async fetchData() {
            if (this.loading) return;

            try {
                this.loading = true;
                this.loadError = "";

                const params = this.buildCustomerQueryParams()

                const res = await http.get(`/customers?${params.toString()}`);

                this.totalPages = res.data.total_pages || 0;
                this.totalCount = res.data.total_count || res.data.total_records || 0;
                this.customers = this.getFormatter(res.data.result || []);

                // Update sorting values from response if available
                if (res.data.sorting) {
                    // Only update if the values from API are different than what we sent
                    // This handles cases where API defaults to different values
                    if (res.data.sorting.primary) {
                        const { field, direction } = res.data.sorting.primary;
                        if (field && this.visibleSortFields.includes(field)) {
                            this.sortField = field;
                        }
                        if (direction && ['asc', 'desc'].includes(direction)) {
                            this.sortDirection = direction;
                        }
                    }

                    if (res.data.sorting.secondary) {
                        const { field, direction } = res.data.sorting.secondary;
                        if (field && this.availableSortFields.includes(field)) {
                            this.secondarySortField = field;
                        }
                        if (direction && ['asc', 'desc'].includes(direction)) {
                            this.secondarySortDirection = direction;
                        }
                    }
                }
            } catch (err) {
                console.error("Error fetching customers", err);
                this.loadError = err.response?.data?.message || "Failed to load customers.";
                this.$toast.error(err.response?.data?.message || "Failed to load customers");
            } finally {
                this.loading = false;
            }
        }
    }
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.pagination-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 16px;

    .pagination-info {
        font-size: 14px;
        color: rgba($textColor, 0.7);
    }
}

:deep(.dark) {
    .pagination-info {
        color: rgba($darkTextColor, 0.7);
    }
}
</style>
